/**
 * Admin Users Hooks
 *
 * Re-exports all admin user hooks and provides the UniversalEntityPage adapter.
 *
 * Split into focused modules for maintainability:
 *   - admin-users-queries.ts   — useAdminUsers, useAdminUser
 *   - admin-users-mutations.ts — useCreateAdminUser, useUpdateAdminUser, useDeleteAdminUser
 *   - admin-users-access.ts    — grant/revoke employee & client access
 */
export * from "./admin-users-queries";
export * from "./admin-users-mutations";
export * from "./admin-users-access";

import { useCallback, useMemo } from "react";
import { useAdminUsers } from "./admin-users-queries";
import { useAdminUser } from "./admin-users-queries";
import {
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
} from "./admin-users-mutations";
import type { User } from "@/lib/types/user";
import type { App_Users } from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";

/** Params accepted by the admin-user list/pagination hooks. */
interface AdminUsersParams extends Record<string, unknown> {
  search?: string;
  role?: string;
  is_active?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * The query layer returns the hand-rolled `User` row shape; the
 * UniversalEntityPage contract is the codegen `App_Users` entity
 * (per ENTITY_HOOKS_USE_CODEGEN_TYPES). Bridge the two at the data boundary.
 */
const asEmployeeEntities = (users: User[] | undefined): App_Users[] =>
  (users ?? []) as unknown as App_Users[];

const asEmployeeEntity = (user: User | undefined): App_Users | undefined =>
  user ? (user as unknown as App_Users) : undefined;

/**
 * Entity hooks adapter for UniversalEntityPage integration.
 */
/**
 * Entity hooks adapter for UniversalEntityPage integration.
 *
 * Allocated ONCE at module scope — the object is a dependency of
 * `useBuildActionContext`'s `useMemo` and closes over nothing render-scoped.
 * See `contacts-graphql.ts` for the full reasoning.
 */
const ADMIN_USER_ENTITY_HOOKS: EntityHooks<App_Users, AdminUsersParams> = {
  usePaginated: (params?: Partial<AdminUsersParams>) => {
    const result = useAdminUsers(params);
    return useMemo(() => {
      const data = asEmployeeEntities(result.data);
      return {
        data: result.data ? { data, totalCount: data.length } : undefined,
        isLoading: result.isLoading,
        error: result.error,
        refetch: result.refetch,
      };
    }, [result.data, result.isLoading, result.error, result.refetch]);
  },
  useInfinite: () => EMPTY_INFINITE_RESULT,
  useSingle: (id: string) => {
    const result = useAdminUser(id);
    return useMemo(
      () => ({
        data: asEmployeeEntity(result.data),
        isLoading: result.isLoading,
        error: result.error,
        refetch: result.refetch,
      }),
      [result.data, result.isLoading, result.error, result.refetch]
    );
  },
  useCreate: () => {
    const mutation = useCreateAdminUser();
    const create = mutation.mutateAsync;
    const mutateAsync = useCallback(
      async (data: Partial<App_Users> & { id?: string }) =>
        asEmployeeEntity(await create(data as unknown as Partial<User>)) as App_Users,
      [create]
    );
    return useStableMutationResult(mutateAsync, mutation.isPending);
  },
  useUpdate: () => {
    const mutation = useUpdateAdminUser();
    const update = mutation.mutateAsync;
    const mutateAsync = useCallback(
      async (data: Partial<App_Users> & { id: string }) =>
        asEmployeeEntity(
          await update({
            id: data.id,
            updates: data as unknown as Partial<User>,
          })
        ) as App_Users,
      [update]
    );
    return useStableMutationResult(mutateAsync, mutation.isPending);
  },
  useDelete: () => {
    const mutation = useDeleteAdminUser();
    const remove = mutation.mutateAsync;
    const mutateAsync = useCallback(
      (id: string) => remove(id).then(() => id),
      [remove]
    );
    return useStableMutationResult(mutateAsync, mutation.isPending);
  },
};

export const useAdminUserHooks = (): EntityHooks<App_Users, AdminUsersParams> =>
  ADMIN_USER_ENTITY_HOOKS;
